import {
  BlotterAggFunc,
  EMPTY_SIZE_VALUE,
  getAvailableMarginSizeValue,
  getCustomerDrawerOptions,
  getTradingStatus,
  type AvailableMarginRow,
  type ColumnDef,
  type Customer,
  type EntityAdminClass,
  type InputsAndDropdownsDrawerOption,
  type SizeValue,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import type { CustomerPosition } from './types';

export const CUSTOMER_POSITIONS_COLUMNS: ColumnDef<CustomerPosition>[] = [
  {
    type: 'customer',
    field: 'Counterparty',
    title: 'Customer',
    initialHide: true,
  },
  {
    type: 'marketAccount',
    field: 'MarketAccount',
    title: 'Market Account',
    initialWidth: 150,
    initialHide: true, // grouped column already shows the market account
  },
  {
    type: 'security',
    field: 'Symbol',
    title: 'Symbol',
    initialWidth: 150,
  },
  {
    type: 'custom',
    id: 'TradingStatus',
    title: 'Trading Status',
    params: {
      valueGetter: ({ data }: ValueGetterParams<CustomerPosition>) => getTradingStatus(data),
    },
  },
  {
    type: 'size',
    field: 'Amount',
    title: 'Position',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'PositionCurrency',
    },
  },
  {
    type: 'price',
    field: 'AvgCost',
    title: 'Average Cost',
    params: {
      assetField: 'Symbol',
    },
  },
  {
    type: 'price',
    field: 'MarkPrice',
    title: 'Mark Price',
    params: {
      assetField: 'Symbol',
    },
  },
  {
    type: 'size',
    field: 'UnrealizedPnL',
    title: 'Unrealized P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
  {
    type: 'size',
    field: 'RealizedPnL',
    title: 'Realized P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
  {
    type: 'size',
    field: 'FundingPnL',
    title: 'Funding P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
  {
    type: 'size',
    field: 'UsedMargin',
    title: 'Used Margin',
    aggregate: true,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'BuyLockedMargin',
    title: 'Buy Locked Margin',
    aggregate: true,
    initialHide: true,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'SellLockedMargin',
    title: 'Sell Locked Margin',
    aggregate: true,
    initialHide: true,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'number',
    field: 'Leverage',
    title: 'Leverage',
    params: {
      currency: 'x',
    },
  },
  {
    type: 'size',
    field: 'Collateral',
    title: 'Collateral',
    aggregate: true,
    initialAggFunc: BlotterAggFunc.MaxSize,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'MaximumExposure',
    title: 'Maximum Exposure',
    aggregate: true,
    initialAggFunc: BlotterAggFunc.MaxSize,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'Equity',
    title: 'Equity',
    aggregate: true,
    initialAggFunc: BlotterAggFunc.MaxSize,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'NextFundingPayment',
    title: 'Next Funding Payment',
    aggregate: true,
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'number',
    field: 'MarginCallThreshold',
    title: 'Margin Call Threshold',
    params: {
      multiplier: 100,
      currency: '%',
    },
  },
  {
    type: 'number',
    field: 'LiquidationThreshold',
    title: 'Liquidation Threshold',
    initialHide: true,
    params: {
      multiplier: 100,
      currency: '%',
    },
  },
  {
    type: 'currency',
    field: 'MarginCurrency',
    title: 'Margin Currency',
  },
  {
    type: 'size',
    id: 'AvailableMargin',
    title: 'Available Margin',
    aggregate: true,
    initialAggFunc: BlotterAggFunc.UniformSize,
    params: {
      valueGetter: getCustomerPositionAvailableMarginSizeValue,
    },
  },
  {
    type: 'size',
    field: 'AvailableMarginToBuy',
    title: 'Available Margin to Buy',
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'AvailableMarginToSell',
    title: 'Available Margin to Sell',
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'AggregateUnrealizedPnL',
    title: 'Aggregate Unrealized P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
  {
    type: 'size',
    field: 'AggregateLockedMargin',
    title: 'Aggregate Locked Margin',
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'size',
    field: 'AggregateUsedMargin',
    title: 'Aggregate Used Margin',
    params: {
      currencyField: 'MarginCurrency',
    },
  },
  {
    type: 'number',
    field: 'StressFactor',
    title: 'Stress Factor',
  },
  {
    type: 'size',
    field: 'NegativeStressPnL',
    title: 'Negative Stress P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
  {
    type: 'size',
    field: 'PositiveStressPnL',
    title: 'Positive Stress P&L',
    aggregate: true,
    params: {
      securityField: 'Symbol',
      securityFieldProperty: 'QuoteCurrency',
      isValueAlreadyNotional: () => true,
    },
  },
];

export function getCustomerPositionsDrawerOptions({
  customers,
}: {
  customers: Customer[];
}): InputsAndDropdownsDrawerOption<CustomerPosition>[] {
  return [
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Customer',
      getIsRequired: () => true,
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
      disabledWhenEditing: true,
    },
    {
      field: 'MarketAccount',
      title: 'Market Account',
      type: 'input',

      disabledWhenEditing: true,
    },
    {
      field: 'Symbol',
      title: 'Symbol',
      type: 'input',
      guideText: 'Security for the balance entry when non-zero.',
      disabledWhenEditing: true,
    },
  ];
}

type CustomerPositionRowData = EntityAdminClass<CustomerPosition>;

export type CustomerPositionValueGetterParams = ValueGetterParams<CustomerPositionRowData, SizeValue>;

export function getCustomerPositionAvailableMarginSizeValue(params: CustomerPositionValueGetterParams): SizeValue {
  const { data } = params;

  if (data == null) {
    return EMPTY_SIZE_VALUE;
  }

  const availableMarginFields: AvailableMarginRow = {
    MarginCurrency: get(data, 'MarginCurrency') ?? undefined,
    AvailableMarginToBuy: get(data, 'AvailableMarginToBuy') ?? undefined,
    AvailableMarginToSell: get(data, 'AvailableMarginToSell') ?? undefined,
  };

  // If none of the source fields are present, render an empty size cell.
  if (
    !availableMarginFields.MarginCurrency &&
    !availableMarginFields.AvailableMarginToBuy &&
    !availableMarginFields.AvailableMarginToSell
  ) {
    return EMPTY_SIZE_VALUE;
  }

  // Shared helper computes min(buy, sell) and attaches currency metadata.
  return getAvailableMarginSizeValue(availableMarginFields, params.context);
}
